import * as React from 'react';

import {
  CREDIT_CARD,
  SALESMATE_RENEWAL_FORM_URL,
  SCHOOL_PREMIUM_SCHOLARSHIP,
} from './constants';

interface PlanExpiresCardProps {
  subscriptionStatus?: {
    payment_method: string;
    purchaser_email: string;
    expired?: boolean;
  } | null;
  subscriptionType: string;
  authorityLevel?: boolean;
}

// Renders the "Once your current plan expires…" left-column card from the
// subscriptions page spec. Mirrors the message logic that used to live
// inline in current_subscription.tsx's onceYourPlanExpires(), but with the
// "contact us" link surfaced as a dedicated button when applicable, and
// each guidance sentence rendered as its own paragraph so the card matches
// the multi-paragraph structure shown in the Figma spec.
const PlanExpiresCard = ({
  subscriptionStatus,
  subscriptionType,
  authorityLevel,
}: PlanExpiresCardProps) => {
  // Only suppress the card when there's no active subscription. Auto-renewing
  // and queued-successor flows still show this card per product direction —
  // the renewal CTA stays relevant regardless of the right-side panel copy.
  if (!subscriptionStatus) { return null }
  if (subscriptionStatus.expired) { return null }

  const { payment_method, purchaser_email, } = subscriptionStatus
  const baseText = `Once your current ${subscriptionType} subscription expires, you will be downgraded to Quill Basic.`

  // Each entry becomes its own <p>. The first is always the base downgrade
  // notice; subsequent entries depend on payment method + authority.
  const bodyParagraphs: React.ReactNode[] = [baseText]
  let contactButton: React.ReactNode = null

  const isScholarshipNonAuthority = subscriptionType === SCHOOL_PREMIUM_SCHOLARSHIP && !authorityLevel
  const isCreditCardAuthority = payment_method === CREDIT_CARD && authorityLevel
  const isCreditCardNonAuthority = payment_method === CREDIT_CARD && !authorityLevel
  const isNonAuthorityWithPurchaser = !authorityLevel && !!purchaser_email && payment_method !== CREDIT_CARD

  if (isScholarshipNonAuthority) {
    // No additional guidance — scholarships can't be renewed by the teacher.
  } else if (isCreditCardAuthority) {
    bodyParagraphs.push('To prevent your subscription from expiring, turn on automatic renewal.')
  } else if (isCreditCardNonAuthority) {
    bodyParagraphs.push(
      <span>
        To prevent your subscription from expiring, contact the purchaser at{' '}
        <a className="focus-on-light" href={`mailto:${purchaser_email}`}>{purchaser_email}</a>{' '}
        and ask them to turn on automatic renewal.
      </span>
    )
  } else if (isNonAuthorityWithPurchaser) {
    bodyParagraphs.push(
      <span>
        To renew your subscription for next year, contact the purchaser at{' '}
        <a className="focus-on-light" href={`mailto:${purchaser_email}`}>{purchaser_email}</a>{' '}
        or <a className="focus-on-light" href="mailto:sales@quill.org">the Quill team</a>.
      </span>
    )
  } else {
    // Default path: any premium subscription (including trials) where the
    // viewer can act on renewal. Salesmate handles trials → paid as well.
    bodyParagraphs.push('To renew your subscription for next year, contact us.')
    contactButton = (
      <a
        className="quill-button small green outlined focus-on-light contact-us-button"
        href={SALESMATE_RENEWAL_FORM_URL}
        rel="noopener noreferrer"
        target="_blank"
      >
        Contact us
      </a>
    )
  }

  return (
    <section aria-label="Subscription expiration notice" className="plan-expires-card">
      {bodyParagraphs.map((paragraph, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={i}>{paragraph}</p>
      ))}
      {contactButton && <div className="contact-us-row">{contactButton}</div>}
    </section>
  )
}

export default PlanExpiresCard
